<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { AUTHMODELHS } from '@/utils/models'
import { useAuthStore } from '@/stores/auth.store'
import { useOrganisationStore } from '@/stores/organisation.store'
import ModalLayoutAuth from '@/layouts/ModelLayoutAuth.vue'
import OnboardingPersonalDetails from '@/components/header/auth/OnboardingFlow/OnboardingPersonalDetails.vue'
import OnboardingProfessionalDetails from '@/components/header/auth/OnboardingFlow/OnboardingProfessionalDetails.vue'
import TeacherOnboarding from '@/components/header/auth/OnboardingFlow/TeacherOnboarding.vue'
import AIModel from '@/components/header/auth/OnboardingFlow/AIModel.vue'
import {
  ONBOARDING_PROFESSIONAL_ROLES,
  ONBOARDING_STEPS
} from '@/components/header/auth/OnboardingFlow/enum/onboarding.enum'
import { USER_STATUS } from '@/components/organisation/enum/user.enums'
import type IAssociation from '@/components/organisation/interface/IAssociation'
import { isEmpty } from 'lodash-es'

interface FilledValues {
  firstName: string
  lastName: string
  occupation: string
}

const organisationStore = useOrganisationStore()
const authStore = useAuthStore()
const stepsValues = ref<FilledValues>({
  firstName: '',
  lastName: '',
  occupation: ''
})
const steps = ref<ONBOARDING_STEPS>(ONBOARDING_STEPS.PERSONAL)
const stepNumber = ref<number>(1)
const occupation = ref<string | undefined>('')

const invitations = computed(() => {
  return organisationStore.associations?.filter((asso: IAssociation) => {
    return asso.status == USER_STATUS.PENDING_USER_ACCEPTANCE
  })
})

const alreadyCreatedOrganization = computed(() => {
  if (organisationStore.instituteOwn) {
    return true
  } else {
    return false
  }
})

const isCheckingOnboardingValues = computed(() => {
  return isEmpty(
    authStore.onboardingSteps.firstName ||
      authStore.onboardingSteps.lastName ||
      authStore.onboardingSteps.occupation
  )
})

/**
 * Set the current onboarding steps
 * @param value update the current onboarding step
 * @param filledValues update latest values
 */
const currentOnboardingSteps = async (value: ONBOARDING_STEPS, filledValues: FilledValues) => {
  steps.value = value
  stepsValues.value.firstName = filledValues.firstName
  stepsValues.value.lastName = filledValues.lastName
  stepsValues.value.occupation = filledValues.occupation
}
/**
 * Update steps current value
 * @param value steps current value
 */
const updateStepsValueFromInit = (value: number) => {
  switch (value) {
    case 1:
      steps.value = ONBOARDING_STEPS.PERSONAL
      break
    case 2:
      steps.value = ONBOARDING_STEPS.PROFESSIONAL
      break
    case 3:
      if (occupation.value == ONBOARDING_PROFESSIONAL_ROLES.TEACHERORPROFRESSIONAL) {
        steps.value = ONBOARDING_STEPS.TEACHER_ORGANISATION
      } else {
        steps.value = ONBOARDING_STEPS.SOFTWARE_DEVELOPER_PRO_PLAN
      }
      break
    default:
      steps.value = ONBOARDING_STEPS.PERSONAL
  }
}

watch(
  () => !isCheckingOnboardingValues.value,
  () => {
    stepNumber.value = authStore.onboardingSteps.step
    occupation.value = authStore.onboardingSteps.occupation
    updateStepsValueFromInit(stepNumber.value)
  }
)
</script>

<template>
  <ModalLayoutAuth :hs="AUTHMODELHS.ONBOARDING">
    <div v-if="steps === ONBOARDING_STEPS.PERSONAL">
      <OnboardingPersonalDetails
        :currentOnboardingSteps="currentOnboardingSteps"
        :prefilledValues="stepsValues"
      />
    </div>
    <div v-if="steps === ONBOARDING_STEPS.PROFESSIONAL">
      <OnboardingProfessionalDetails
        :currentOnboardingSteps="currentOnboardingSteps"
        :prefilledValues="stepsValues"
        :invitations="invitations"
        :alreadyCreatedOrganization="alreadyCreatedOrganization"
      />
    </div>
    <div v-if="steps === ONBOARDING_STEPS.TEACHER_ORGANISATION">
      <TeacherOnboarding
        :currentOnboardingSteps="currentOnboardingSteps"
        :prefilledValues="stepsValues"
      />
    </div>
    <div v-if="steps === ONBOARDING_STEPS.SOFTWARE_DEVELOPER_PRO_PLAN">
      <AIModel
        :currentOnboardingSteps="currentOnboardingSteps"
        :prefilledValues="stepsValues"
        :invitations="invitations"
      />
    </div>
  </ModalLayoutAuth>
</template>
